import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useIntlAccaTournaments } from "./useIntlAccaTournaments.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types.ts";

export interface RollLeg {
  id: string;
  match_id: string;
  market_type: string;
  selection: string;
  strikza_price: number | null;
  market_price_at_selection: number;
  fair_probability: number | null;
  reasoning_note: string;
  result: "WON" | "LOST" | "VOID" | null;
}

export interface Roll {
  id: string;
  tier_code: "SINGLES" | "ACCA3" | "ACCA5" | "ACCA10";
  tier_label: string;
  leg_count: number;
  window_days: number;
  combined_odds: number | null;
  status: "ACTIVE" | "WON" | "LOST" | "VOID" | "EXPIRED";
  selection_basis: "CALIBRATED" | "MARKET_LED" | "AGENT_REVIEWED";
  generated_at: string;
  window_start: string | null;
  window_end: string | null;
  window_seq: number | null;
  legs: RollLeg[];
}

export interface IntlAccaRollsVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  rolls: Roll[];
  matchLabels: Record<string, string>;
  matchCodes: Record<string, { home: string | null; away: string | null }>;
  refetch: () => Promise<void>;
}

export function useIntlAccaRolls(): IntlAccaRollsVM {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const { tournaments, activeId } = useIntlAccaTournaments();
  const tournamentId = activeId ?? tournaments[0]?.id ?? null;
  const [s, setS] = useState<Omit<IntlAccaRollsVM, "refetch">>({
    loading: true, error: null, tournament: null, rolls: [], matchLabels: {}, matchCodes: {},
  });

  const fetchAll = useCallback(async () => {
    if (!tournamentId) {
      setS({ loading: false, error: null, tournament: null, rolls: [], matchLabels: {}, matchCodes: {} });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const [tRes, rRes] = await Promise.all([
        supabase.from("intl_acca_tournaments").select("*").eq("id", tournamentId).maybeSingle(),
        supabase.from("intl_acca_rolls").select("*")
          .eq("tournament_id", tournamentId).eq("status", "ACTIVE")
          .order("generated_at", { ascending: false }),
      ]);
      if (tRes.error) throw tRes.error;
      const rolls = (rRes.data ?? []) as Roll[];

      // Keep only the most recent set (same generated window)
      // Group by tier_code, take first (most recent) of each
      const byTier = new Map<string, Roll>();
      for (const r of rolls) {
        if (!byTier.has(r.tier_code)) byTier.set(r.tier_code, r);
      }
      const latestRolls = [...byTier.values()].sort((a, b) => {
        const order = ["SINGLES", "ACCA3", "ACCA5", "ACCA10"];
        return order.indexOf(a.tier_code) - order.indexOf(b.tier_code);
      });

      const rollIds = latestRolls.map((r) => r.id);
      const lgRes = rollIds.length
        ? await supabase.from("intl_acca_roll_legs").select("*").in("roll_id", rollIds)
        : { data: [], error: null };
      const legs = (lgRes.data ?? []) as Array<RollLeg & { roll_id: string }>;
      const byRoll = new Map<string, RollLeg[]>();
      for (const l of legs) {
        if (!byRoll.has(l.roll_id)) byRoll.set(l.roll_id, []);
        byRoll.get(l.roll_id)!.push(l);
      }
      const withLegs = latestRolls.map((r) => ({ ...r, legs: byRoll.get(r.id) ?? [] }));

      // Resolve match labels
      const matchIds = [...new Set(legs.map((l) => l.match_id))];
      const labels: Record<string, string> = {};
      const matchCodes: Record<string, { home: string | null; away: string | null }> = {};
      if (matchIds.length) {
        const mRes = await supabase.from("intl_acca_matches")
          .select("match_id,home,away,home_slot,away_slot").in("match_id", matchIds);
        type MRow = {
          match_id: string; home: string | null; away: string | null;
          home_slot: string | null; away_slot: string | null;
        };
        const codes = new Set<string>();
        for (const m of (mRes.data ?? []) as MRow[]) {
          if (m.home) codes.add(m.home);
          if (m.away) codes.add(m.away);
        }
        const nRes = codes.size
          ? await supabase.from("nations").select("code,name").in("code", [...codes])
          : { data: [] };
        const nameBy: Record<string, string> = {};
        for (const n of (nRes.data ?? []) as Array<{ code: string; name: string }>) {
          nameBy[n.code] = n.name;
        }
        const side = (code: string | null, slot: string | null) =>
          (code && (nameBy[code] ?? code)) || slot || "TBD";
        for (const m of (mRes.data ?? []) as MRow[]) {
          labels[m.match_id] = `${side(m.home, m.home_slot)} v ${side(m.away, m.away_slot)}`;
          matchCodes[m.match_id] = { home: m.home, away: m.away };
        }
      }

      setS({
        loading: false, error: null,
        tournament: (tRes.data as IntlAccaTournament | null) ?? null,
        rolls: withLegs, matchLabels: labels, matchCodes,
      });
    } catch (e) {
      setS({
        loading: false, error: e instanceof Error ? e.message : String(e),
        tournament: null, rolls: [], matchLabels: {}, matchCodes: {},
      });
    }
  }, [supabase, tournamentId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
